/**
 * The set of all valid trip status codes as defined by the `kinetik-trip-service` repository.
 */
export const TRIP_STATUS_CODES = Object.freeze({
  DENIED: 'DENIED', // EOL
  APPROVAL_PENDING: 'APPROVAL_PENDING',
  APPROVAL_DENIED: 'APPROVAL_DENIED', // EOL
  READY_TO_ASSIGN: 'READY_TO_ASSIGN',
  EXPIRED: 'EXPIRED', // EOL
  ASSIGNMENT_PENDING: 'ASSIGNMENT_PENDING',
  ASSIGNMENT_EXPORTED: 'ASSIGNMENT_EXPORTED',
  ASSIGNMENT_ERROR: 'ASSIGNMENT_ERROR',
  ASSIGNMENT_ACCEPTED: 'ASSIGNMENT_ACCEPTED',
  ASSIGNMENT_REJECTED: 'ASSIGNMENT_REJECTED',
  CANCELLED: 'CANCELLED',
  ASSIGNMENT_CANCELLED: 'ASSIGNMENT_CANCELLED',
  ASSIGNMENT_UNAVAILABLE: 'ASSIGNMENT_UNAVAILABLE',
  ASSIGNMENT_DISPATCHED: 'ASSIGNMENT_DISPATCHED',
  REQUESTER_CANCELLED: 'REQUESTER_CANCELLED', // EOL
  PATIENT_CANCELLED: 'PATIENT_CANCELLED', // EOL
  EN_ROUTE: 'EN_ROUTE',
  ARRIVED_AT_PU: 'ARRIVED_AT_PU',
  POB: 'POB',
  NO_SHOW: 'NO_SHOW', // EOL
  PROVIDER_NO_SHOW: 'PROVIDER_NO_SHOW', // EOL
  COMPLETED: 'COMPLETED', // EOL
});

export const TRIP_STATUS_LABELS = Object.freeze({
  [TRIP_STATUS_CODES.DENIED]: 'Denied',
  [TRIP_STATUS_CODES.APPROVAL_PENDING]: 'Approval Pending',
  [TRIP_STATUS_CODES.APPROVAL_DENIED]: 'Approval Denied',
  [TRIP_STATUS_CODES.READY_TO_ASSIGN]: 'Ready to Assign',
  [TRIP_STATUS_CODES.EXPIRED]: 'Expired',
  [TRIP_STATUS_CODES.ASSIGNMENT_PENDING]: 'Assignment Pending',
  [TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED]: 'Assignment Exported',
  [TRIP_STATUS_CODES.ASSIGNMENT_ERROR]: 'Assignment Error',
  [TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED]: 'Accepted',
  [TRIP_STATUS_CODES.ASSIGNMENT_REJECTED]: 'Assignment Rejected',
  [TRIP_STATUS_CODES.ASSIGNMENT_CANCELLED]: 'Assignment Cancelled',
  [TRIP_STATUS_CODES.CANCELLED]: 'Cancelled',
  [TRIP_STATUS_CODES.ASSIGNMENT_UNAVAILABLE]: 'Assignment Unavailable',
  [TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED]: 'Assignment Dispatched',
  [TRIP_STATUS_CODES.REQUESTER_CANCELLED]: 'Requester Cancelled',
  [TRIP_STATUS_CODES.PATIENT_CANCELLED]: 'Member Cancelled',
  [TRIP_STATUS_CODES.EN_ROUTE]: 'Trip In Progress',
  [TRIP_STATUS_CODES.ARRIVED_AT_PU]: 'Trip In Progress',
  [TRIP_STATUS_CODES.POB]: 'Trip In Progress',
  [TRIP_STATUS_CODES.NO_SHOW]: 'Member No Show',
  [TRIP_STATUS_CODES.PROVIDER_NO_SHOW]: 'Provider No Show',
  [TRIP_STATUS_CODES.COMPLETED]: 'Completed',
});

/**
 * The set of trip status codes that indicate a trip is eligible for assignment.
 */
export const ASSIGNMENT_ELIGIBLE_TRIP_STATUS_CODES = Object.freeze(
  new Set([
    TRIP_STATUS_CODES.READY_TO_ASSIGN,
    TRIP_STATUS_CODES.ASSIGNMENT_PENDING,
    TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED,
    TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
    TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
  ]),
);

/**
 * The set of trip status codes that indicate a trip is eligible for re-assignment.
 */
export const REASSIGNMENT_ELIGIBLE_TRIP_STATUS_CODES = Object.freeze(
  new Set([
    TRIP_STATUS_CODES.ASSIGNMENT_ERROR,
    TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
    TRIP_STATUS_CODES.ASSIGNMENT_REJECTED,
    TRIP_STATUS_CODES.ASSIGNMENT_CANCELLED,
    TRIP_STATUS_CODES.ASSIGNMENT_UNAVAILABLE,
    TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
  ]),
);

/**
 * The set of trip status codes that indicate a trip is actively in progress.
 */
export const ACTIVE_TRIP_STATUS_CODES = Object.freeze(
  new Set([
    TRIP_STATUS_CODES.EN_ROUTE,
    TRIP_STATUS_CODES.ARRIVED_AT_PU,
    TRIP_STATUS_CODES.POB,
  ]),
);

/**
 * The set of trip status codes that indicate a trip was cancelled and therefore never
 * took place. A cancelled trip does not consume a member's coverage allotment, so these
 * are excluded from coverage trip-limit counting.
 *
 * `REQUESTER_CANCELLED` and `PATIENT_CANCELLED` are end-of-life statuses that are no
 * longer assigned to new trips, but historical trips still carry them — they must stay
 * in this set for prior-year counts to be correct.
 *
 * `CANCELLED` is included defensively only. It is absent from `tripStatusCodes` in both
 * kinetik-trip-service and kinetik-config-service, and the Trip model's `status` field
 * enumerates that service's list — so no trip can actually persist it today.
 *
 * `ASSIGNMENT_CANCELLED` is deliberately NOT included. This repo's own
 * `REASSIGNMENT_ELIGIBLE_TRIP_STATUS_CODES` (above) classifies it as a still-live trip
 * awaiting reassignment, not a terminal cancellation. Excluding it would understate a
 * member's real pending utilization and could let them exceed their true annual limit
 * once the stalled trip resolves.
 * TODO(trip-service domain owner): confirm this classification. If `ASSIGNMENT_CANCELLED`
 * is in fact terminal in kinetik-trip-service, add it back to this set.
 */
export const CANCELLED_TRIP_STATUS_CODES = Object.freeze(
  new Set([
    TRIP_STATUS_CODES.CANCELLED,
    TRIP_STATUS_CODES.REQUESTER_CANCELLED,
    TRIP_STATUS_CODES.PATIENT_CANCELLED,
  ]),
);

/**
 * The set of trip status codes kinetik-trip-service treats as terminal
 * (`tripTerminalStatusCodes`, the `// EOL` entries above). Bulk-terminate skips
 * trips in these statuses, so they never count toward what a cancel targets.
 */
export const TERMINAL_TRIP_STATUS_CODES = Object.freeze(
  new Set([
    TRIP_STATUS_CODES.EXPIRED,
    TRIP_STATUS_CODES.DENIED,
    TRIP_STATUS_CODES.APPROVAL_DENIED,
    TRIP_STATUS_CODES.NO_SHOW,
    TRIP_STATUS_CODES.PROVIDER_NO_SHOW,
    TRIP_STATUS_CODES.REQUESTER_CANCELLED,
    TRIP_STATUS_CODES.PATIENT_CANCELLED,
    TRIP_STATUS_CODES.COMPLETED,
  ]),
);
